import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const PackagesSection = () => {
  const { t } = useTranslation();

  // Standard-Features für Starter und Professional
  const standardFeatures = [
    'Sortimente pro Kunde',
    'Produktbilder und Varianten',
    'Aktuelle Lagerdaten & Verfügbarkeit',
    'Komplexe Preislisten pro Kunde',
    'Kundenlisten pro Mitarbeiter',
    'Mehrere Warenkörbe pro Kunde',
    'Kompletter Offline-Modus',
    'Einfache Installation über App-Store (soon)'
  ];

  // Enterprise-Features
  const enterpriseFeatures = [
    'Individuelle ERP Integration',
    'Eigene App im eigenen CI',
    'App als B2B-Shop für Kunden',
    'Integration ins Active Directory',
    'Mehrsprachigkeit für internationale Teams',
    'On-Promise Hosting',
  ];

  const plans = [
    {
      title: 'Starter',
      description: 'Perfekt für kleine Teams mit bis zu 5 Außendienstmitarbeitern.',
      price: '249€',
      period: 'pro Monat',
      users: '5 Benutzer inklusive',
      features: standardFeatures,
      buttonText: 'Starter auswählen',
      featured: false
    },
    {
      title: 'Professional',
      description: 'Ideal für wachsende Unternehmen mit bis zu 15 Außendienstmitarbeitern.',
      price: '449€',
      period: 'pro Monat',
      users: '15 Benutzer inklusive',
      features: standardFeatures,
      buttonText: 'Professional auswählen',
      featured: true
    },
    {
      title: 'Enterprise',
      description: 'Maßgeschneiderte Lösung für Unternehmen mit besonderen Anforderungen.',
      price: 'Individuell',
      period: 'nach Anfrage',
      users: 'Unbegrenzte Benutzer',
      features: [...standardFeatures, ...enterpriseFeatures],
      buttonText: 'Angebot anfordern',
      featured: false,
      isEnterprise: true
    }
  ];

  return (
    <section id="pricing" className="py-20 bg-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            Wähle das passende Paket für deinen Außendienst
          </h2>
          <p className="text-xl text-gray-700">
            Alle Pakete enthalten die komplette Salescockpit-Funktionalität – der Unterschied liegt in der Anzahl der inkludierten Benutzer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={cn(
                "bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1",
                plan.featured && "ring-2 ring-blue-500 ring-offset-2 ring-offset-blue-50 md:scale-105"
              )}
            >
              {plan.featured && (
                <div className="bg-blue-600 text-white py-2 text-center text-sm font-semibold">
                  MEISTGEWÄHLT
                </div>
              )}
              
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-2 text-gray-900">{plan.title}</h3>
                <p className="text-gray-600 mb-4 h-12">{plan.description}</p>
                
                {/* Benutzeranzahl prominent anzeigen */}
                <div className="bg-blue-50 rounded-lg p-3 mb-6">
                  <p className="text-blue-700 font-semibold text-center">{plan.users}</p>
                </div>
                
                <div className="mb-6">
                  <span className="text-4xl font-bold text-blue-600">{plan.price}</span>
                  <span className="text-gray-600 ml-2">{plan.period}</span>
                </div>
                
                {/* Features */}
                <div className="mb-8">
                  {!plan.isEnterprise ? (
                    // Standard Features für Starter und Professional
                    <>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Alle Features inklusive:</h4>
                      <ul className="space-y-2">
                        {plan.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start">
                            <svg className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    // Enterprise Features aufgeteilt
                    <>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Alle Standard-Features plus:</h4>
                      <ul className="space-y-2 mb-4">
                        {enterpriseFeatures.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start">
                            <svg className="h-4 w-4 text-blue-500 mr-2 mt-1 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700 text-sm font-medium">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
                
                <Button
                  className={cn(
                    "w-full py-6 text-base font-semibold",
                    plan.featured ? "bg-blue-600 hover:bg-blue-700 text-white" : "border-2 border-blue-600 bg-transparent text-blue-600 hover:bg-blue-50"
                  )}
                  asChild
                >
                  <a href="#contact">{plan.buttonText}</a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-lg text-gray-700 mb-6">
            Alle Preise verstehen sich zzgl. MwSt. Bei jährlicher Zahlung gewähren wir 10% Rabatt.
          </p>
          <p className="text-blue-700 font-semibold">
            Nicht sicher, welches Paket das richtige ist? Wir beraten dich gerne!
          </p>
          <Button
            size="lg"
            className="mt-6 bg-blue-900 hover:bg-blue-800 text-white"
            asChild
          >
            <a href="#contact">Kostenlose Beratung vereinbaren</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection; 